"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "AdoReporter", {
    enumerable: true,
    get: function() {
        return AdoReporter;
    }
});
const _formathrtime = require("@lage-run/format-hrtime");
const _isTargetStatusLogEntry = require("./isTargetStatusLogEntry.js");
const _logger = require("@lage-run/logger");
const _chalk = /*#__PURE__*/ _interop_require_default(require("chalk"));
const _slowestTargetRuns = require("./slowestTargetRuns.js");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
const colors = {
    [_logger.LogLevel.info]: _chalk.default.white,
    [_logger.LogLevel.verbose]: _chalk.default.gray,
    [_logger.LogLevel.warn]: _chalk.default.white,
    [_logger.LogLevel.error]: _chalk.default.white,
    [_logger.LogLevel.silly]: _chalk.default.green,
    task: _chalk.default.cyan,
    pkg: _chalk.default.magenta,
    ok: _chalk.default.green,
    error: _chalk.default.red,
    warn: _chalk.default.yellow
};
const logLevelLabel = {
    [_logger.LogLevel.info]: "INFO",
    [_logger.LogLevel.warn]: "WARN",
    [_logger.LogLevel.error]: "ERR!",
    [_logger.LogLevel.silly]: "SILLY",
    [_logger.LogLevel.verbose]: "VERB"
};
function getTaskLogPrefix(pkg, task) {
    return `${colors.pkg(pkg)} ${colors.task(task)}`;
}
function normalize(prefixOrMessage, message) {
    if (typeof message === "string") {
        const prefix = prefixOrMessage;
        return {
            prefix,
            message
        };
    } else {
        const prefix = "";
        const message = prefixOrMessage;
        return {
            prefix,
            message
        };
    }
}
function format(level, prefix, message) {
    return `${logLevelLabel[level]}: ${prefix} ${message}\n`;
}
class AdoReporter {
    log(entry) {
        if (entry.data && entry.data.target && entry.data.target.hidden) {
            return;
        }
        if (entry.data && entry.data.target) {
            if (!this.logEntries.has(entry.data.target.id)) {
                this.logEntries.set(entry.data.target.id, []);
            }
            this.logEntries.get(entry.data.target.id).push(entry);
        }
        if (this.options.logLevel >= entry.level) {
            if (this.options.grouped && entry.data?.target) {
                return this.logTargetEntryByGroup(entry);
            }
            return this.logTargetEntry(entry);
        }
    }
    logTargetEntry(entry) {
        const colorFn = colors[entry.level];
        const data = entry.data;
        if ((0, _isTargetStatusLogEntry.isTargetStatusLogEntry)(data)) {
            const { target, hash, duration } = data;
            const { packageName, task } = target;
            const normalizedArgs = this.options.grouped ? normalize(entry.msg) : normalize(getTaskLogPrefix(packageName ?? "<root>", task), entry.msg);
            const pkgTask = this.options.grouped ? `${_chalk.default.magenta(packageName)} ${_chalk.default.cyan(task)}` : "";
            switch(data.status){
                case "running":
                    return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn(`${colors.ok("➔")} start ${pkgTask}`)));
                case "success":
                    return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn(`${colors.ok("✓")} done ${pkgTask} - ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(duration))}`)));
                case "failed":
                    return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn(`${colors.error("✖")} fail ${pkgTask}`)));
                case "skipped":
                    return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn(`${colors.ok("»")} skip ${pkgTask} - ${hash}`)));
                case "aborted":
                    return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn(`${colors.warn("-")} aborted ${pkgTask}`)));
                case "queued":
                    return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn(`${colors.warn("…")} aborted ${pkgTask}`)));
            }
        } else if (entry?.data?.target) {
            const { target } = data;
            const { packageName, task } = target;
            const normalizedArgs = this.options.grouped ? normalize(entry.msg) : normalize(getTaskLogPrefix(packageName ?? "<root>", task), entry.msg);
            return this.logStream.write(format(entry.level, normalizedArgs.prefix, colorFn("|  " + normalizedArgs.message)));
        } else if (entry?.msg.trim() !== "") {
            return this.logStream.write(format(entry.level, "", entry.msg));
        }
    }
    logTargetEntryByGroup(entry) {
        const data = entry.data;
        const target = data.target;
        const { id } = target;
        this.groupedEntries.set(id, this.groupedEntries.get(id) || []);
        this.groupedEntries.get(id)?.push(entry);
        if ((0, _isTargetStatusLogEntry.isTargetStatusLogEntry)(data)) {
            if (data.status === "success" || data.status === "failed" || data.status === "skipped" || data.status === "aborted") {
                const { status, duration } = data;
                this.logStream.write(`##[group] ${colors.pkg(data.target.packageName ?? "<root>")} ${colors.task(data.target.task)} ${status}${duration ? `, took ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(duration))}` : ""}\n`);
                const entries = this.groupedEntries.get(id);
                for (const targetEntry of entries){
                    this.logTargetEntry(targetEntry);
                }
                this.logStream.write(`##[endgroup]\n`);
            }
        }
    }
    summarize(schedulerRunSummary) {
        const { targetRuns, targetRunByStatus, duration } = schedulerRunSummary;
        const { failed, aborted, skipped, success, pending } = targetRunByStatus;
        const statusColorFn = {
            success: _chalk.default.greenBright,
            failed: _chalk.default.redBright,
            skipped: _chalk.default.gray,
            running: _chalk.default.yellow,
            pending: _chalk.default.gray,
            aborted: _chalk.default.red,
            queued: _chalk.default.magenta
        };
        this.logStream.write(_chalk.default.cyanBright(`##[section]Summary\n`));
        if (targetRuns.size > 0) {
            const slowestTargets = (0, _slowestTargetRuns.slowestTargetRuns)([
                ...targetRuns.values()
            ]);
            for (const wrappedTarget of slowestTargets){
                const colorFn = statusColorFn[wrappedTarget.status];
                const target = wrappedTarget.target;
                this.logStream.write(format(_logger.LogLevel.info, getTaskLogPrefix(target.packageName || "[GLOBAL]", target.task), colorFn(`${wrappedTarget.status}${wrappedTarget.duration ? `, took ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(wrappedTarget.duration))}` : ""}`)));
            }
            this.logStream.write(`[Tasks Count] success: ${success.length}, skipped: ${skipped.length}, pending: ${pending.length}, aborted: ${aborted.length}\n`);
        } else {
            this.logStream.write("Nothing has been run.\n");
        }
        if (failed && failed.length > 0) {
            let packagesMessage = `##vso[task.logissue type=error]Your build failed on the following packages => `;
            for (const targetId of failed){
                const target = targetRuns.get(targetId)?.target;
                if (target) {
                    const { packageName, task } = target;
                    const taskLogs = this.logEntries.get(targetId);
                    packagesMessage += `[${packageName} ${task}], `;
                    this.logStream.write(`##[error] [${_chalk.default.magenta(packageName)} ${_chalk.default.cyan(task)}] ${_chalk.default.redBright("ERROR DETECTED")}\n`);
                    if (taskLogs) {
                        for (const entry of taskLogs){
                            // Log each entry separately to prevent truncation
                            this.logStream.write(`##[error] ${entry.msg}\n`);
                        }
                    }
                }
            }
            packagesMessage += "find the error logs above with the prefix '##[error]!'\n";
            this.logStream.write(packagesMessage);
        }
        this.logStream.write(format(_logger.LogLevel.info, "", `Took a total of ${(0, _formathrtime.formatDuration)((0, _formathrtime.hrToSeconds)(duration))} to complete`));
    }
    constructor(options){
        _define_property(this, "options", void 0);
        _define_property(this, "logStream", void 0);
        _define_property(this, "logEntries", void 0);
        _define_property(this, "groupedEntries", void 0);
        this.options = options;
        this.logStream = process.stdout;
        this.logEntries = new Map();
        this.groupedEntries = new Map();
        options.logLevel = options.logLevel || _logger.LogLevel.info;
    }
}
